import { Alert, Box, Divider, Heading, HStack, Text, useDisclosure, VStack } from '@televet/kibble-ui';
import UnsavedFormChangesModal, { PageType } from 'pages/Forms/components/UnsavedFormChangesModal';
import React, { ForwardRefExoticComponent, RefAttributes, useEffect, useRef, useState } from 'react';
import { NavLink, Prompt, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { RouteDefinitions } from 'routes';
import { Location } from 'history';
import CollapsibleFooter from 'shared/components/CollapsibleFooter';
import { LeavingCurrentPage } from 'pages/Settings/hooks/useSettingPageDetail';
import EligibleItems from './components/EligibleItems';
import Rewards from './components/Rewards';
import Settings from './components/Settings';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import usePersistedState from 'shared/hooks/usePersistedState';
import { LoyaltyProgramStatus, useGetLoyaltyProgramBasicInfoQuery } from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { LoyaltyEvents } from 'shared/providers/TrackingProvider/EventNames';
import { useTracking } from 'shared/providers/TrackingProvider';

enum LoyaltyProgramSection {
  Settings = 'Settings',
  Rewards = 'Rewards',
  EligibleItems = 'Eligible Items',
}

enum LoyaltyProgramRelativePaths {
  Settings = '/settings',
  Rewards = '/rewards',
  Eligibleitems = '/eligible-items',
}

interface ISection {
  name: LoyaltyProgramSection;
  relativePath: LoyaltyProgramRelativePaths;
  description?: string;
  component?:
    | ForwardRefExoticComponent<UnsavedChangesProps & RefAttributes<unknown>>
    | ForwardRefExoticComponent<ViewProps & RefAttributes<unknown>>;
}

export interface ViewProps {
  viewRef: React.MutableRefObject<HTMLDivElement | null>;
}

export interface UnsavedChangesProps {
  setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
  setAreChangesValid: React.Dispatch<React.SetStateAction<boolean>>;
  viewRef?: React.MutableRefObject<HTMLDivElement | null>;
}

const sections: ISection[] = [
  {
    name: LoyaltyProgramSection.Settings,
    relativePath: LoyaltyProgramRelativePaths.Settings,
    description: 'Adjust the settings for your loyalty program.',
    component: Settings,
  },
  {
    name: LoyaltyProgramSection.Rewards,
    relativePath: LoyaltyProgramRelativePaths.Rewards,
    component: Rewards,
  },
  {
    name: LoyaltyProgramSection.EligibleItems,
    relativePath: LoyaltyProgramRelativePaths.Eligibleitems,
    component: EligibleItems,
  },
];

const loyaltyProgramStatus: { [key: string]: string } = {
  [LoyaltyProgramStatus.Live]: 'On',
  [LoyaltyProgramStatus.Draft]: 'Off',
  [LoyaltyProgramStatus.Test]: 'in Test Mode',
};

export const LoyaltyProgram = (): JSX.Element => {
  const { path } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { currentClinicId } = useClinicUser();
  const loyaltyProgramPageRef = useRef<LeavingCurrentPage>(null);
  const viewRef = useRef<HTMLDivElement | null>(null);
  const [hasUnsavedChange, setHasUnsavedChange] = useState(false);
  const [areChangesValid, setAreChangesValid] = useState(true);
  const [lastLocation, setLastLocation] = useState<Location>(location);
  const [onNavChangeConfirmed, setOnNavChangeConfirmed] = useState(false);
  const [, setHasUnsavedChangePersisted] = usePersistedState(
    PersistedStateKeys.HasUnsavedSettingsChanges,
    hasUnsavedChange,
  );
  const {
    isOpen: isUnsavedChangesOpen,
    onOpen: onUnsavedChangesOpen,
    onClose: onUnsavedChangesClose,
  } = useDisclosure();
  const { trackEvent } = useTracking();

  const handleBlockedNavigation = (location: Location): boolean => {
    if (hasUnsavedChange) {
      onUnsavedChangesOpen();
      setLastLocation(location);
      return false;
    }
    return true;
  };

  const onLeaveCurrentPage = (): void => {
    onUnsavedChangesClose();
    setOnNavChangeConfirmed(true);
    setHasUnsavedChange(false);
    loyaltyProgramPageRef.current?.onLeavingCurrentPage();
    history.push(lastLocation?.pathname);
  };

  useEffect(() => {
    const handleBrowserClosed = (event: BeforeUnloadEvent): string => {
      event.preventDefault();
      return (event.returnValue = 'Are you sure you want to exit?');
    };

    {
      hasUnsavedChange && window.addEventListener('beforeunload', handleBrowserClosed);
    }

    return (): void => {
      window.removeEventListener('beforeunload', handleBrowserClosed);
    };
  }, [hasUnsavedChange]);

  useEffect(() => {
    if (onNavChangeConfirmed && lastLocation) {
      history.push(lastLocation?.pathname);
      onUnsavedChangesClose();
      setOnNavChangeConfirmed(false);
    }
  }, [onNavChangeConfirmed, lastLocation, history, onUnsavedChangesClose]);

  useEffect(() => {
    setHasUnsavedChangePersisted(hasUnsavedChange);
    if (!hasUnsavedChange) {
      onUnsavedChangesClose();
    }
  }, [hasUnsavedChange, onUnsavedChangesClose, setHasUnsavedChangePersisted]);

  useEffect(() => {
    // Set settings route by default
    if (history.location.pathname === RouteDefinitions.LoyaltyProgram) {
      history.push(`${path}${LoyaltyProgramRelativePaths.Settings}`);
      trackEvent(LoyaltyEvents.SettingsView);
    }
  }, [history, path, trackEvent]);

  const { data: loyaltyProgramData } = useGetLoyaltyProgramBasicInfoQuery({
    variables: {
      where: {
        participatingClinics: {
          some: {
            id: {
              equals: currentClinicId,
            },
          },
        },
        status: {
          not: {
            equals: LoyaltyProgramStatus.Archived,
          },
        },
      },
    },
    skip: !currentClinicId,
  });

  const programStatus = loyaltyProgramData?.findFirstLoyaltyProgram?.status;
  const [showProgramStatusBanner, setShowProgramStatusBanner] = useState(true);

  return (
    <HStack alignItems="stretch" height="100%">
      <VStack
        minWidth="234px"
        maxWidth="250px"
        width="100%"
        alignItems="flex-start"
        borderRight="1px"
        borderRightColor="border.default"
        flex="0 0 auto"
        overflowY="auto"
      >
        {sections.map((section) => {
          const isActive = history.location.pathname === `${path}${section.relativePath}`;
          return (
            <NavLink
              to={`${path}${section.relativePath}`}
              key={section.relativePath}
              style={{ width: '100%' }}
              onClick={(): void => trackEvent(LoyaltyEvents.PageViewed, { pageName: section.name })}
            >
              <Box
                key={section.relativePath}
                {...(isActive && { bgColor: 'background.primaryAlpha.hover' })}
                w="100%"
                py={3}
                px={5}
              >
                <Text
                  color={isActive ? 'text.brand' : 'text.default'}
                  size="md"
                  variant={isActive ? 'default' : 'subtle'}
                >
                  {section.name}
                </Text>
              </Box>
            </NavLink>
          );
        })}
      </VStack>
      <Switch>
        {sections.map((section) => {
          return (
            <Route path={`${path}${section.relativePath}`} key={section.relativePath}>
              <VStack w="100%" marginInlineStart="0 !important">
                <Box overflowY="scroll" w="100%" p={5} ref={viewRef} h="100%">
                  <Heading size="md" mb={5}>
                    {section.name}
                  </Heading>
                  <Divider w="100%" />
                  {section.description && (
                    <Text as="p" size="sm" variant="subtle" mt={3} maxW={['100%', '530px']}>
                      {section.description}
                    </Text>
                  )}
                  {programStatus &&
                    section.relativePath !== LoyaltyProgramRelativePaths.Settings &&
                    showProgramStatusBanner && (
                      <Alert
                        title={`Your Loyalty Program is ${loyaltyProgramStatus[programStatus]}`}
                        status="warning"
                        iconProps={{ name: 'infoCircle' }}
                        mt={2}
                        onClose={(): void => setShowProgramStatusBanner(false)}
                      />
                    )}
                  {section.component && (
                    <Box mt={8}>
                      <section.component
                        setHasUnsavedChange={setHasUnsavedChange}
                        setAreChangesValid={setAreChangesValid}
                        ref={loyaltyProgramPageRef}
                        viewRef={viewRef}
                      />
                    </Box>
                  )}
                </Box>
                {section.relativePath !== LoyaltyProgramRelativePaths.Rewards && (
                  <>
                    <Prompt message={handleBlockedNavigation} />
                    <UnsavedFormChangesModal
                      pageType={PageType.Setting}
                      isOpen={isUnsavedChangesOpen}
                      onClose={onUnsavedChangesClose}
                      onConfirm={onLeaveCurrentPage}
                    />
                    <CollapsibleFooter
                      isLoading={false}
                      isDisabled={!areChangesValid}
                      onSave={(): void => {
                        setHasUnsavedChange(false), loyaltyProgramPageRef.current?.onClickedSaveButton();
                      }}
                      onCancel={(): void => {
                        setHasUnsavedChange(false), loyaltyProgramPageRef.current?.onClickedCancelButton();
                      }}
                      hasChanges={hasUnsavedChange}
                    />
                  </>
                )}
              </VStack>
            </Route>
          );
        })}
      </Switch>
    </HStack>
  );
};
